import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Tab, Tabs } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { TabPanel } from '../common';
import clientGraphql from '../../common/apollo-graphql';
import GlobalLoader from '../common/components/loader/GlobalLoader';
import ApiService from '../common/services/ApiService';
import SendFaxForm from './components/SendFaxForm';
import SentFaxTable from './components/SentFaxTable';
import { REFRESH_SENT_FAX_STATUS, SAVE_SENT_FAX, SEND_SENT_FAX } from './gql/Mutation';
import { FETCH_SENT_FAXES } from './gql/Query';

export default function SendFaxReferrals() {
  const [tab, setTab] = useState(() => {
    try {
      return sessionStorage.getItem('sendFax.activeTab') || 'send';
    } catch (e) {
      return 'send';
    }
  });

  const hasAutoRefreshedPendingRef = useRef(false);
  const sendInProgressRef = useRef(false);

  const { data, loading, error, refetch } = useQuery(FETCH_SENT_FAXES, {
    client: clientGraphql,
    variables: { limit: 200 },
    fetchPolicy: 'network-only',
  });

  const [saveSentFax, { loading: saving }] = useMutation(SAVE_SENT_FAX, {
    client: clientGraphql,
  });
  const [sendSentFax, { loading: sending }] = useMutation(SEND_SENT_FAX, {
    client: clientGraphql,
  });
  const [refreshSentFaxStatus, { loading: refreshing }] = useMutation(REFRESH_SENT_FAX_STATUS, {
    client: clientGraphql,
  });

  const isGlobalLoading = loading || saving || sending || refreshing;

  const sentFaxes = useMemo(() => {
    const rows = data?.sent_faxes ?? [];

    return rows.map(item => ({
      id: item.id,
      recipient: item.recipient || '',
      faxNumber: item.fax_number || '',
      message: item.message || '',
      details: item.details || '',
      attachmentNames: item.attachment_names || [],
      attachmentUrls: item.attachment_urls || [],
      dateSent: item.sent_at || item.created_at,
      completedAt: item.completed_at || null,
      status: item.status || 'Queued',
      statusLabel: item.status_label || item.status || 'Queued',
      providerFaxId: item.provider_fax_id || null,
    }));
  }, [data]);

  const handleAddFax = useCallback(async (fax) => {
    if (sendInProgressRef.current) {
      throw new Error('A fax is already being sent. Please wait.');
    }

    sendInProgressRef.current = true;

    try {
      let uploadedAttachments = [];
      const attachmentItems = (fax.attachments || []).filter(item => item?.file);
      const reportAttachmentProgress = typeof fax.onAttachmentProgress === 'function'
        ? fax.onAttachmentProgress
        : null;

      for (const attachment of attachmentItems) {
        if (reportAttachmentProgress) {
          reportAttachmentProgress(attachment.id, {
            status: 'uploading',
            progress: 0,
          });
        }

        try {
          const uploadRes = await ApiService.uploadSentFaxAttachments(
            [attachment.file],
            (progress) => {
              if (reportAttachmentProgress) {
                reportAttachmentProgress(attachment.id, {
                  status: 'uploading',
                  progress,
                });
              }
            }
          );

          const uploaded = uploadRes?.attachments?.[0];
          if (!uploaded) {
            throw new Error('Attachment upload failed.');
          }

          uploaded.name = (attachment.displayName || uploaded.name || '').trim() || uploaded.name;
          uploadedAttachments.push(uploaded);

          if (reportAttachmentProgress) {
            reportAttachmentProgress(attachment.id, {
              status: 'done',
              progress: 100,
              displayName: uploaded.name,
            });
          }
        } catch (uploadError) {
          if (reportAttachmentProgress) {
            reportAttachmentProgress(attachment.id, {
              status: 'failed',
              progress: 0,
            });
          }
          throw uploadError;
        }
      }

      const saveRes = await saveSentFax({
        variables: {
          recipient: fax.recipient,
          faxNumber: fax.faxNumber,
          message: fax.message || null,
          details: fax.details || null,
          status: fax.status || 'Queued',
          attachments: uploadedAttachments.map(item => JSON.stringify(item)),
        },
      });

      const savedFaxId = saveRes?.data?.save_sent_fax?.id;
      if (savedFaxId) {
        await sendSentFax({
          variables: {
            sentFaxId: savedFaxId,
          },
        });
      }

      await refetch();
      setTab('sent');
    } finally {
      sendInProgressRef.current = false;
    }
  }, [refetch, saveSentFax, sendSentFax]);

  const handleRefreshFaxStatus = useCallback(async (sentFaxId) => {
    await refreshSentFaxStatus({
      variables: {
        sentFaxId,
      },
    });

    await refetch();
  }, [refreshSentFaxStatus, refetch]);

  const handleResendFax = useCallback(async (sentFaxId) => {
    await sendSentFax({
      variables: {
        sentFaxId,
      },
    });

    await refetch();
  }, [sendSentFax, refetch]);

  useEffect(() => {
    try {
      sessionStorage.setItem('sendFax.activeTab', tab);
    } catch (e) {
      // no-op
    }
  }, [tab]);

  useEffect(() => {
    if (tab !== 'sent' || loading || refreshing || hasAutoRefreshedPendingRef.current) {
      return;
    }

    const pendingIds = sentFaxes
      .filter(item => {
        const value = String(item.status ?? '').trim();
        return value !== '' && !Number.isNaN(Number(value)) && Number(value) < 0;
      })
      .map(item => item.id);

    hasAutoRefreshedPendingRef.current = true;

    if (pendingIds.length === 0) {
      return;
    }

    (async () => {
      await Promise.allSettled(
        pendingIds.map((sentFaxId) => refreshSentFaxStatus({
          variables: { sentFaxId },
        }))
      );

      await refetch();
    })();
  }, [tab, loading, refreshing, sentFaxes, refreshSentFaxStatus, refetch]);

  return (
    <Box sx={{ width: '100%', maxWidth: 1280, mx: 'auto' }}>
      {isGlobalLoading && (
        <GlobalLoader label={sending ? 'Sending fax...' : (refreshing ? 'Refreshing fax status...' : (saving ? 'Saving fax...' : 'Loading sent faxes...'))} />
      )}
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error.message || 'Failed to load sent faxes.'}</Alert> : null}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} textColor="secondary">
          <Tab label="Send Fax" value="send" />
          <Tab label="Sent Fax" value="sent" />
        </Tabs>
      </Box>

      <TabPanel value={tab} index="send">
        <SendFaxForm onSend={handleAddFax} saving={saving || sending} />
      </TabPanel>

      <TabPanel value={tab} index="sent">
        <SentFaxTable rows={sentFaxes} onRefresh={handleRefreshFaxStatus} onResend={handleResendFax} />
      </TabPanel>
    </Box>
  );
}
